<template>
  <div>
    <geolocation-input
      v-if="showSearchInput"
      ref="geolocationInput"
      :is-inline="inlineSearchResults"
      :loading="searchResults.areLoading"
      @geolocated="search"
    />
    <div
      v-else-if="searchResults.areLoading"
      class="extra-spacing extra-spacing"
    >
      <div class="loader" />
    </div>

    <ul
      v-if="!searchResults.areLoading && searchResults.locations.length > 0"
      class="location-list"
      :class="listClass"
    >
      <li
        v-for="location in searchResults.locations"
        :key="location.storeId"
        :class="[
          'location-list__item',
          { 'location-list__item--selected': showSelected && selectedStoreId === location.storeId }
        ]"
      >
        <div class="location-list__item-section">
          <div class="location-list__item-section-left">
            <h3 class="title title--5">
              {{ location.description }}
            </h3>
          </div>

          <div class="location-list__item-section-right">
            <div class="layout layout--horizontal">
              <span
                v-if="!showingAllLocations"
                class="smallest dark-gray no-wrap"
              >{{ $miles(location.distance) }}</span>

              <directions-button
                class="icon icon--link icon--smedium fa-directions"
                :location="location"
              >
                <span class="ada-text">Get Directions</span>
              </directions-button>

              <button
                v-if="showStoreInfo"
                title="View store details"
                type="button"
                class="button button--small button--icon button--light auto-spacing-right"
                @click="$openModal('locationDetails', { location, hideOrderingModes: true })"
              >
                <span class="icon fa-info dark-gray" />
              </button>
            </div>
          </div>
        </div>

        <div class="location-list__item-section location-list__item-section--expand">
          <p class="smaller">
            {{ formatLocationAddress(location.address) }}
          </p>
        </div>

        <div class="location-list__item-section location-list__item-section--align-bottom">
          <div class="location-list__item-section-left">
            <dl class="definition-list">
              <template v-if="location.hasStoreHours">
                <dt class="definition-list__term">
                  Hours
                </dt>
                <dd class="definition-list__description">
                  <current-day-store-hours :location="location" />
                </dd>
              </template>

              <template v-if="location.phoneNumber">
                <dt class="definition-list__term">
                  Phone
                </dt>
                <dd class="definition-list__description">
                  <a
                    v-if="location.phoneNumber"
                    class="link primary"
                    :href="`tel:${location.phoneNumber}`"
                  >
                    {{ $phone(location.phoneNumber) }}
                  </a>
                </dd>
              </template>
            </dl>
          </div>

          <div
            v-if="selectable"
            class="location-list__item-section-right"
          >
            <div
              v-if="showSelected
                && selectedStoreId === location.storeId
                && !isSelecting"
              class="layout layout--icon layout--inline no-spacing no-select"
            >
              <span class="icon icon--small fa-check gray" />
              <p class="smaller uppercase bold gray">Selected</p>
            </div>

            <button
              v-else
              :class="[
                'button button--auto-width button--small',
                {
                  'button--loading': location.storeId === locationIdBeingSelected && isSelecting,
                  'button--primary': !showStoreValidations || location.currentDayOperatingSchedule.status === 'Online'
                }
              ]"
              type="button"
              :disabled="showStoreValidations && !location.searchedAttributes.supported"
              @click="select(location)"
            >
              {{ getLocationSelectionText(location) }}
            </button>
          </div>
        </div>

        <div
          v-if="showStoreValidations && location.searchedAttributes.messages.length > 0"
          class="location-list__item-section location-list__item-section--expand"
        >
          <p class="layout layout--icon">
            <span class="icon danger far fa-exclamation-circle" />
            <ul class="smaller location-list__error-message-list">
              <li
                v-for="(message, index) in location.searchedAttributes.messages"
                :key="index"
              >
                {{ message }}
              </li>
            </ul>
          </p>
        </div>
      </li>
    </ul>

    <button
      v-if="!searchResults.areLoading && searchResults.moreResultsExist || searchResults.moreResultsAreLoading"
      :class="[
        'button button--large button--light normalcase spacing-top',
        { 'button--loading': searchResults.moreResultsAreLoading }
      ]"
      type="button"
      @click="showMoreResults"
    >
      Show More
    </button>

    <transition name="fade-down-only-in">
      <p v-if="hasSearched && !searchResults.areLoading && searchResults.locations.length === 0">
        Sorry, no locations were found within {{ searchDistance }} miles.
      </p>
    </transition>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  import { formatAddressInline } from '@/helpers/address-format';

  export default {
    name: 'LocationSelector',

    props: {
      isSelecting: {
        type: Boolean,
        default: false
      },

      inlineSearchResults: {
        type: Boolean,
        default: false
      },

      listClass: {
        type: String,
        default: null
      },

      selectable: {
        type: Boolean,
        default: true
      },

      showStoreInfo: {
        type: Boolean,
        default: false
      },

      showAllStores: {
        type: Boolean,
        default: false
      },

      /**
       * This prop is used to force the search input to be shown,
       * even if the merchant config is set to show all stores.
       */
      showSearch: {
        type: Boolean,
        default: false
      },

      showSelected: {
        type: Boolean,
        default: true
      },

      selectedStoreId: {
        type: Number,
        default: null
      },
      /**
       * Since this component is also used to select a favorite store,
       * we want to hide the validations regardless of the stores operating hours.
      */
      showStoreValidations: {
        type: Boolean,
        default: true
      }
    },

    data() {
      return {
        hasSearched: false,
        locationIdBeingSelected: null,
        searchDistance: null,
        showingAllLocations: true
      };
    },

    computed: {
      ...mapGetters('locations', ['searchResults']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order']),

      showSearchInput() {
        return this.showSearch || !this.showingAllLocations;
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.$store.dispatch('locations/resetSearchLocations');

        this.loadAllStores();

        this.searchDistance = this.merchant.options.searchRadiusInMiles || 50;
      },

      formatLocationAddress(address) {
        if (!address) return '';
        return formatAddressInline(address, address.countryCode);
      },

      async loadAllStores() {
        /**
         * merchant config to indicate that a user should only be able to select
         * from all possible stores instead of being able to search for a store
         * or use their location to view their closest stores
         */
        const { showAllStores: showAllStoresConfig } = this.merchant.options;
        /**
         * component prop to indicate that a user should
         * be able to select from all possible stores
         */
        const { showAllStores: showAllStoresProp } = this;

        this.showingAllLocations = showAllStoresConfig || showAllStoresProp;

        if (this.showingAllLocations) {
          /**
           * "Show all stores" has no dedicated all-locations endpoint, so we
           * approximate it with a geo-radius search centered on the continental
           * US. The radius must reach every store the merchant has regardless of
           * country: any two points on Earth are within ~12,430 miles, so a
           * radius above that covers the entire globe (incl. international
           * merchants such as those in Australia).
           */
          await this.$store.dispatch('locations/searchLocations', {
            distance: 20000,
            latitude: 39.50,
            longitude: -98.35
          });
        }

        else {
          this.$store.dispatch('locations/resetSearchLocations');
        }
      },

      async search(coordinates) {
        if (!coordinates) {
          this.hasSearched = false;
          this.loadAllStores();
        }
        else {
          await this.$store.dispatch('locations/searchLocations', {
            distance: this.searchDistance,
            latitude: coordinates.latitude,
            longitude: coordinates.longitude
          });

          this.hasSearched = true;
        }
      },

      select(location) {
        this.locationIdBeingSelected = location.storeId;

        this.$emit('select', location);

        // this.$triggerGoogleTrackingEvent('SelectLocation', 'Locations');
      },

      async showMoreResults() {
        await this.$store.dispatch('locations/showMoreSearchLocations');
      },

      getLocationSelectionText(location) {
        const selectionTextMap = {
          IMMEDIATE_ORDERING: 'Select',
          FUTURE_SAME_DAY_ORDERING: 'Order for Later Today',
          FUTURE_NEXT_DAY_ORDERING: 'Order for Later',
          UNAVAILABLE_ORDERING: 'View Menu'
        };

        return this.showStoreValidations
          ? selectionTextMap[location.locationOrderingStatus]
          : selectionTextMap.IMMEDIATE_ORDERING;
      }
    }
  };
</script>
